import { Fragment, useCallback, useEffect, useState, type ReactNode } from 'react'
import { Alert, Button, Chip, Input, ToggleButton, ToggleButtonGroup } from '@heroui/react'
import { api, ApiError } from '../lib/api'
import {
  AREAS,
  BREAK_KINDS,
  BREAK_REASON_MAX,
  HANDOFF_LABEL_MAX,
  handoffLabelProblem,
  handoffWithoutPoint,
  optionsIn,
  reasonNameProblem,
  reasonsInOrder,
  type BreakKind,
  type BreakReason,
  type ConfigMode,
  type ConfigProblem,
  type CounterPolicy,
  type ModeView,
  type OptionArea,
  type OptionsView,
  type OrgOption,
} from '../lib/orgOptions'
import { explain } from '../lib/staffErrors'
import { hasPhrase, type StaffKey } from '../lib/staffText'
import type { Service } from '../lib/types'
import { Icon } from '../ui/icons'
import { useSession } from '../ui/Session'
import { useStaffText } from '../ui/StaffText'
import { useFlash } from './sectionKit'
import { ChoiceSelect, Notice, PageHead, SettingBlock, SettingRow, SettingsCard, SwitchRow } from './settingsKit'
import '../styles/orgset.css'

/**
 * General (#options): the organisation's switches in one place, grouped by where they show.
 * Each source saves through its own route: /options (B4), /config/mode (B1),
 * /counter-policy and /break-reasons (B2).
 */

const AREA_TITLE: Record<OptionArea, StaffKey> = {
  counter: 'org.opt.counterGroup',
  reception: 'org.opt.receptionGroup',
  booking: 'org.opt.bookingGroup',
  other: 'org.opt.otherGroup',
}

/** Our own words for the options this build knows; the server's label for any other. */
const KNOWN: Record<string, { name: StaffKey; hint: StaffKey }> = {
  handoff: { name: 'org.opt.handoff', hint: 'org.opt.handoffHint' },
  outOfTurnCall: { name: 'org.opt.outOfTurnCall', hint: 'org.opt.outOfTurnCallHint' },
  callStartsServing: { name: 'org.opt.callStartsServing', hint: 'org.opt.callStartsServingHint' },
  onlineBooking: { name: 'org.opt.onlineBooking', hint: 'org.opt.onlineBookingHint' },
  priorityTickets: { name: 'org.opt.priorityTickets', hint: 'org.opt.priorityTicketsHint' },
}

const KIND_NAME: Record<BreakKind, StaffKey> = {
  lunch: 'counter.kind.lunch',
  short_break: 'counter.kind.short_break',
  task: 'counter.kind.task',
}

type Say = (key: StaffKey, vars?: Record<string, string | number>) => string

export function Options({ organisation, onSaved }: { organisation?: string; onSaved?: () => void }) {
  const { lang, say } = useStaffText()
  const session = useSession()
  const identity = session?.identity
  const { flash, toast } = useFlash()
  const [view, setView] = useState<OptionsView | null>(null)
  const [mode, setMode] = useState<ModeView | null>(null)
  const [policy, setPolicy] = useState<CounterPolicy | null>(null)
  const [reasons, setReasons] = useState<BreakReason[] | null>(null)
  // Only to say when hand-off has no point to send to; unread stays null and says nothing.
  const [services, setServices] = useState<Service[] | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  // Desk rules and reasons are written by branch.manage across the whole organisation (B2).
  const pinned = (identity?.staff.branchIds ?? []).length > 0 && !identity?.staff.owner
  const mayDeskRules = Boolean(identity?.permissions.includes('branch.manage')) && !pinned

  const load = useCallback(async () => {
    const [options, configMode, counter, breaks] = await Promise.allSettled([
      api.orgOptions(organisation),
      api.configMode(organisation),
      api.counterPolicy(organisation),
      api.breakReasons(organisation),
    ])
    // A refusal (403) means the section is not this person's: it stays hidden, unexplained.
    const failed = (r: PromiseSettledResult<unknown>) =>
      r.status === 'rejected' && !(r.reason instanceof ApiError && r.reason.status === 403) ? r.reason : null
    if (options.status === 'fulfilled') setView(options.value)
    if (configMode.status === 'fulfilled') setMode(configMode.value)
    if (counter.status === 'fulfilled') setPolicy(counter.value.policy)
    if (breaks.status === 'fulfilled') setReasons(breaks.value.reasons)
    const first = [options, configMode, counter, breaks].map(failed).find(Boolean)
    if (first) setLoadError(explain(first, lang, 'org.opt.notLoaded'))
    setLoaded(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisation])

  useEffect(() => {
    setServices(null)
    api
      .catalogueServices(organisation)
      .then((r) => setServices(r.services))
      .catch(() => setServices(null))
  }, [organisation])

  useEffect(() => {
    setView(null)
    setMode(null)
    setPolicy(null)
    setReasons(null)
    setLoaded(false)
    setLoadError('')
    void load()
  }, [load])

  // The screens read options from the session and the mode from Administration.
  const saved = () => {
    void session?.refresh()
    onSaved?.()
  }

  const run = async (fn: () => Promise<void>, said: string): Promise<boolean> => {
    setBusy(true)
    setError('')
    try {
      await fn()
      saved()
      flash(said)
      return true
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
      return false
    } finally {
      setBusy(false)
    }
  }

  const nameOf = (o: OrgOption) => (KNOWN[o.key] ? say(KNOWN[o.key]!.name) : o.label)
  const flip = (o: OrgOption, on: boolean) =>
    void run(async () => {
      setView(await api.saveOrgOptions({ options: { [o.key]: on } }, organisation))
    }, say(on ? 'org.opt.on' : 'org.opt.off', { name: nameOf(o) }))
  const rule = (change: Partial<CounterPolicy>, name: string, on: boolean) =>
    void run(async () => {
      setPolicy((await api.saveCounterPolicy(change, organisation)).policy)
    }, say(on ? 'org.opt.on' : 'org.opt.off', { name }))

  // Off and not bought: the switch cannot turn it on (402), so it is not offered.
  const unlicensed = (o: OrgOption) =>
    Boolean(o.module) && (o.on ? !o.effective : !(identity?.modules ?? []).includes(o.module!))

  const reasonRequired = policy && (
    <SwitchRow
      nested={Boolean(view)}
      label={say('org.opt.reasonRequired')}
      checked={policy.outOfTurnReasonRequired}
      disabled={busy || !mayDeskRules}
      dimmed={(busy || !mayDeskRules) && !policy.outOfTurnReasonRequired}
      onChange={(on) => rule({ outOfTurnReasonRequired: on }, say('org.opt.reasonRequired'), on)}
    />
  )

  const optionRow = (o: OrgOption) => (
    <Fragment key={o.key}>
      <SwitchRow
        label={nameOf(o)}
        description={KNOWN[o.key] ? say(KNOWN[o.key]!.hint) : undefined}
        tag={
          unlicensed(o) ? (
            <Chip size="sm" variant="secondary">
              {say('org.opt.notLicensed')}
            </Chip>
          ) : undefined
        }
        checked={o.on}
        disabled={busy || (!o.on && unlicensed(o))}
        dimmed={(busy || unlicensed(o)) && !o.on}
        onChange={(on) => flip(o, on)}
      />
      {o.key === 'handoff' && handoffWithoutPoint(o.on, services) && (
        <SettingRow nested control label={<span className="os-warn" role="status">{say('org.opt.noHandoffPoint')}</span>}>
          <Button variant="secondary" size="sm" onPress={() => (window.location.hash = 'services')}>
            {say('org.opt.toServices')}
          </Button>
        </SettingRow>
      )}
      {o.key === 'handoff' && o.on && (
        <HandoffLabel
          saved={view!.handoffLabel}
          busy={busy}
          onSave={(handoffLabel) =>
            void run(async () => {
              setView(await api.saveOrgOptions({ handoffLabel }, organisation))
            }, say('org.opt.saved'))
          }
        />
      )}
      {o.key === 'outOfTurnCall' && o.on && reasonRequired}
    </Fragment>
  )

  const counterOptions = view ? optionsIn('counter', view.options) : []
  // Without the options (not the organisation's administrator), the reason rule stands alone.
  const reasonShownAlone = policy && !counterOptions.some((o) => o.key === 'outOfTurnCall')

  return (
    <>
      <PageHead title={say('settings.general')} line={say('settings.general.line')} />
      {toast}
      {loadError && <Notice>{loadError}</Notice>}
      {error && <Notice>{error}</Notice>}

      {mode && (
        <ModeGroup
          mode={mode}
          organisation={organisation}
          mayWrite={identity?.permissions.includes('profiles.manage') ?? false}
          onChanged={(next) => {
            setMode(next)
            saved()
          }}
          flash={flash}
        />
      )}

      {(counterOptions.length > 0 || reasonShownAlone) && (
        <Group title={say(AREA_TITLE.counter)}>
          {counterOptions.map(optionRow)}
          {reasonShownAlone && reasonRequired}
        </Group>
      )}

      {(policy || reasons) && (
        <Group title={say('org.opt.breaksGroup')}>
          {policy && (
            <SwitchRow
              label={say('org.opt.breakApproval')}
              description={say('org.opt.breakApprovalHint')}
              checked={policy.breakApproval}
              disabled={busy || !mayDeskRules}
              dimmed={(busy || !mayDeskRules) && !policy.breakApproval}
              onChange={(on) => rule({ breakApproval: on }, say('org.opt.breakApproval'), on)}
            />
          )}
          {reasons && (
            <BreakReasons
              reasons={reasons}
              organisation={organisation}
              mayWrite={mayDeskRules}
              onChanged={(next) => {
                setReasons(next)
                saved()
              }}
              flash={flash}
            />
          )}
        </Group>
      )}

      {view &&
        AREAS.filter((a) => a !== 'counter').map((area) => {
          const shown = optionsIn(area, view.options)
          if (shown.length === 0) return null
          return (
            <Group key={area} title={say(AREA_TITLE[area])}>
              {shown.map(optionRow)}
            </Group>
          )
        })}

      {loaded && !view && !mode && !policy && !reasons && !loadError && <p className="empty">{say('org.opt.notLoaded')}</p>}
    </>
  )
}

/**
 * The organisation's break reasons, edited in place: rename on Enter or leaving
 * the box, kind from a picker, order by the arrows. Each change is its own call.
 */
function BreakReasons({
  reasons,
  organisation,
  mayWrite,
  onChanged,
  flash,
}: {
  reasons: BreakReason[]
  organisation?: string
  mayWrite: boolean
  onChanged: (next: BreakReason[]) => void
  flash: (text: string) => void
}) {
  const { lang, say } = useStaffText()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [name, setName] = useState('')
  const [kind, setKind] = useState<BreakKind>('short_break')
  const ordered = reasonsInOrder(reasons)
  const newProblem = name.trim() ? reasonNameProblem(name, reasons) : ''

  const reload = async () => onChanged((await api.breakReasons(organisation)).reasons)
  const act = async (fn: () => Promise<unknown>, said?: string) => {
    setBusy(true)
    setError('')
    try {
      await fn()
      await reload()
      if (said) flash(said)
      return true
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
      return false
    } finally {
      setBusy(false)
    }
  }

  // Swapping two neighbours' places, or renumbering when they share one.
  const move = (r: BreakReason, by: -1 | 1) => {
    const i = ordered.findIndex((x) => x.id === r.id)
    const other = ordered[i + by]
    if (!other) return
    const tied = other.sortOrder === r.sortOrder
    void act(async () => {
      if (tied) {
        const next = [...ordered]
        next.splice(i, 1)
        next.splice(i + by, 0, r)
        for (const [n, x] of next.entries()) {
          if (x.sortOrder !== (n + 1) * 10) await api.updateBreakReason(x.id, { sortOrder: (n + 1) * 10 }, organisation)
        }
      } else {
        await api.updateBreakReason(r.id, { sortOrder: other.sortOrder }, organisation)
        await api.updateBreakReason(other.id, { sortOrder: r.sortOrder }, organisation)
      }
    }, say('record.orderSaved'))
  }

  const add = () => {
    if (!name.trim() || newProblem || busy) return
    void act(
      () =>
        api.createBreakReason(
          { name: name.trim(), kind, sortOrder: (ordered.at(-1)?.sortOrder ?? 0) + 10 },
          organisation,
        ),
      say('org.opt.reasonAdded'),
    ).then((ok) => {
      if (ok) setName('')
    })
  }

  return (
    <>
    <SettingRow label={say('org.opt.breakReasons')} description={say('org.opt.breakReasonsHint')} />
    <SettingBlock>
    <div className="os-reasons">
      {ordered.length === 0 ? (
        <p className="os-help">{say('org.opt.noReasons')}</p>
      ) : (
        <ul className="os-items__list">
          {ordered.map((r, i) => (
            <ReasonRow
              key={`${r.id}:${r.name}:${r.kind}`}
              reason={r}
              others={reasons}
              first={i === 0}
              last={i === ordered.length - 1}
              busy={busy}
              mayWrite={mayWrite}
              say={say}
              onRename={(next) => void act(() => api.updateBreakReason(r.id, { name: next }, organisation), say('record.saved'))}
              onKind={(next) => void act(() => api.updateBreakReason(r.id, { kind: next }, organisation), say('record.saved'))}
              onMove={(by) => move(r, by)}
              onDelete={() => void act(() => api.deleteBreakReason(r.id, organisation), say('org.opt.reasonDeleted'))}
            />
          ))}
        </ul>
      )}
      {mayWrite && (
        <form
          className="os-items__add"
          onSubmit={(e) => {
            e.preventDefault()
            add()
          }}
        >
          <Input
            value={name}
            maxLength={BREAK_REASON_MAX + 1}
            aria-label={say('org.opt.reasonNew')}
            placeholder={say('org.opt.reasonNew')}
            aria-invalid={Boolean(newProblem) || undefined}
            onChange={(e) => setName(e.target.value)}
          />
          <span className="os-items__kind">
            <ChoiceSelect
              label={say('org.opt.reasonKind')}
              value={kind}
              onChange={(next) => setKind(next as BreakKind)}
              options={BREAK_KINDS.map((k) => ({ value: k, label: say(KIND_NAME[k]) }))}
            />
          </span>
          <Button type="submit" variant="secondary" isDisabled={busy || !name.trim() || Boolean(newProblem)}>
            <Icon name="plus" size={16} /> {say('org.opt.reasonAdd')}
          </Button>
        </form>
      )}
      {newProblem && <p className="os-items__error">{say(newProblem === 'taken' ? 'org.opt.reasonTaken' : 'org.opt.reasonLong')}</p>}
      {error && <Notice>{error}</Notice>}
    </div>
    </SettingBlock>
    </>
  )
}

function ReasonRow({
  reason,
  others,
  first,
  last,
  busy,
  mayWrite,
  say,
  onRename,
  onKind,
  onMove,
  onDelete,
}: {
  reason: BreakReason
  others: BreakReason[]
  first: boolean
  last: boolean
  busy: boolean
  mayWrite: boolean
  say: Say
  onRename: (name: string) => void
  onKind: (kind: BreakKind) => void
  onMove: (by: -1 | 1) => void
  onDelete: () => void
}) {
  const [name, setName] = useState(reason.name)
  const problem = reasonNameProblem(name, others, reason.id)
  const commit = () => {
    if (name.trim() === reason.name) return
    if (problem) setName(reason.name)
    else onRename(name.trim())
  }
  return (
    <li className="os-items__row">
      <Input
        variant="secondary"
        value={name}
        maxLength={BREAK_REASON_MAX + 1}
        disabled={!mayWrite || busy}
        aria-label={say('org.opt.reasonName')}
        aria-invalid={Boolean(problem) || undefined}
        onChange={(e) => setName(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
          }
          if (e.key === 'Escape') setName(reason.name)
        }}
      />
      <span className="os-items__kind">
        <ChoiceSelect
          size="sm"
          label={say('org.opt.reasonKind')}
          value={reason.kind}
          disabled={!mayWrite || busy}
          onChange={(next) => next !== reason.kind && onKind(next as BreakKind)}
          options={BREAK_KINDS.map((k) => ({ value: k, label: say(KIND_NAME[k]) }))}
        />
      </span>
      {mayWrite && (
        <span className="os-items__acts">
          <Button isIconOnly variant="ghost" size="sm" isDisabled={first || busy} aria-label={say('org.opt.reasonUp', { name: reason.name })} onPress={() => onMove(-1)}>
            <Icon name="up" size={16} />
          </Button>
          <Button isIconOnly variant="ghost" size="sm" isDisabled={last || busy} aria-label={say('org.opt.reasonDown', { name: reason.name })} onPress={() => onMove(1)}>
            <Icon name="down" size={16} />
          </Button>
          <Button isIconOnly variant="ghost" size="sm" isDisabled={busy} aria-label={say('org.opt.reasonDelete', { name: reason.name })} onPress={onDelete}>
            <Icon name="close" size={16} />
          </Button>
        </span>
      )}
    </li>
  )
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return <SettingsCard title={title}>{children}</SettingsCard>
}

/** A question asked in place, with the server's reasons: a dialog to answer when labelled, else a refusal. */
function Ask({ label, title, lines, said, children }: { label?: string; title: string; lines?: string[]; said?: string; children: ReactNode }) {
  return (
    <Alert status="warning" className="os-ask" role={label ? 'alertdialog' : 'alert'} aria-label={label}>
      <Alert.Indicator />
      <Alert.Content>
        <Alert.Title>{title}</Alert.Title>
        {lines && (
          <ul className="os-ask__list">
            {lines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        )}
        {said && <Alert.Description>{said}</Alert.Description>}
        <div className="os-ask__acts">{children}</div>
      </Alert.Content>
    </Alert>
  )
}

function HandoffLabel({ saved, busy, onSave }: { saved: string; busy: boolean; onSave: (label: string) => void }) {
  const { say } = useStaffText()
  const [label, setLabel] = useState(saved)
  useEffect(() => setLabel(saved), [saved])
  const typed = label.trim()
  const bad = handoffLabelProblem(typed)
  return (
      <SettingRow
        as="form"
        onSubmit={(e) => {
          e.preventDefault()
          if (!bad && typed !== saved) onSave(typed)
        }}
        nested
        control
        label={<label htmlFor="os-handoff-label">{say('org.opt.handoffLabel')}</label>}
      >
        {typed !== saved && (
          <Button type="submit" variant="secondary" size="sm" isDisabled={bad || busy}>
            {say('record.save')}
          </Button>
        )}
        <Input
          id="os-handoff-label"
          className="os-input"
          value={label}
          maxLength={HANDOFF_LABEL_MAX}
          placeholder={say('counter.toCashier')}
          onChange={(e) => setLabel(e.target.value)}
        />
      </SettingRow>
  )
}

/** One reason Basic was refused: the server's sentence names the group, queue or
 *  profile ("Group Cash has segments"); ours only when it sent none. */
function problemText(p: ConfigProblem, say: Say): string {
  // A call order is a code (fair_share) in the server's sentence; the editor's label says it.
  const order = `profiles.order.${p.args?.order ?? ''}`
  if (p.code === 'ORDER_NOT_BASIC' && p.args?.profile && hasPhrase(order)) {
    return say('org.opt.orderNotBasic', { profile: p.args.profile, order: say(order as StaffKey) })
  }
  const key = `errors.CONFIG_NOT_BASIC.${p.code}`
  return p.message || (hasPhrase(key) ? say(key as StaffKey, p.args) : p.code)
}

/** Simplifying fixes every reason but several profiles: it cannot choose among them. */
export function simplifies(problems: readonly ConfigProblem[]): boolean {
  return !problems.some((p) => p.code === 'SEVERAL_PROFILES')
}

/** Each reason once: two rules of one profile refuse in the same words. */
export function problemLines(problems: readonly ConfigProblem[], say: Say): string[] {
  return [...new Set(problems.map((p) => problemText(p, say)))]
}

function ModeGroup({
  mode,
  organisation,
  mayWrite,
  onChanged,
  flash,
}: {
  mode: ModeView
  organisation?: string
  mayWrite: boolean
  onChanged: (next: ModeView) => void
  flash: (text: string) => void
}) {
  const { lang, say } = useStaffText()
  const [asking, setAsking] = useState(false)
  // What simplifying to Basic drops, while it waits for a yes.
  const [simplifying, setSimplifying] = useState<ConfigProblem[] | null>(null)
  const [refused, setRefused] = useState<{ said: string; problems: ConfigProblem[]; title?: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const send = async (to: ConfigMode, simplify = false) => {
    setBusy(true)
    setError('')
    try {
      onChanged(await api.setConfigMode(to, organisation, simplify))
      setAsking(false)
      setSimplifying(null)
      setRefused(null)
      flash(say(to === 'basic' ? 'org.opt.nowBasic' : 'org.opt.nowAdvanced'))
    } catch (err) {
      if (err instanceof ApiError && err.code === 'CONFIG_NOT_BASIC') {
        // The answer's own reasons, or the mode read's when the refusal carried none.
        const fresh = err.problems?.length ? null : await api.configMode(organisation).catch(() => null)
        const problems = (err.problems as ConfigProblem[] | undefined)?.length
          ? (err.problems as ConfigProblem[])
          : (fresh?.basicProblems ?? mode.basicProblems)
        if (!simplify && simplifies(problems)) setSimplifying(problems)
        else {
          setSimplifying(null)
          setRefused({ said: explain(err, lang, 'common.unreachable'), problems })
        }
      } else if (err instanceof ApiError && err.code === 'CONFIG_WOULD_STRAND') {
        setSimplifying(null)
        setRefused({
          said: explain(err, lang, 'common.unreachable'),
          problems: (err.problems as ConfigProblem[] | undefined) ?? [],
          title: err.message,
        })
      } else {
        setError(explain(err, lang, 'common.unreachable'))
      }
    } finally {
      setBusy(false)
    }
  }

  const choose = (to: ConfigMode) => {
    if (to === mode.mode || busy) return
    setRefused(null)
    setSimplifying(null)
    // Leaving Basic is always allowed but turns its screens read-only: asked once.
    if (to === 'advanced') setAsking(true)
    else if (mode.basicProblems.length > 0 && simplifies(mode.basicProblems)) setSimplifying(mode.basicProblems)
    else void send('basic')
  }

  return (
    <Group title={say('org.opt.queueGroup')}>
      <SettingRow control id="os-mode" label={say('org.opt.mode')} description={say('org.opt.modeHint')}>
        <ToggleButtonGroup
          aria-labelledby="os-mode"
          size="sm"
          selectionMode="single"
          disallowEmptySelection
          isDisabled={!mayWrite || busy}
          selectedKeys={[asking ? 'advanced' : simplifying ? 'basic' : mode.mode]}
          onSelectionChange={(keys) => {
            const [m] = [...keys]
            if (m === 'basic' || m === 'advanced') choose(m)
          }}
        >
          {(['basic', 'advanced'] as const).map((m, i) => (
            <ToggleButton key={m} id={m}>
              {i > 0 && <ToggleButtonGroup.Separator />}
              {say(m === 'basic' ? 'org.opt.basic' : 'org.opt.advanced')}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </SettingRow>
      {asking && (
        <Ask label={say('org.opt.toAdvanced')} title={say('org.opt.toAdvanced')}>
          <Button variant="primary" size="sm" isDisabled={busy} onPress={() => void send('advanced')}>
            {say('org.opt.switch')}
          </Button>
          <Button variant="ghost" size="sm" onPress={() => setAsking(false)}>
            {say('common.cancel')}
          </Button>
        </Ask>
      )}
      {simplifying && (
        <Ask label={say('org.opt.toBasicSimplify')} title={say('org.opt.toBasicSimplify')} lines={problemLines(simplifying, say)}>
          <Button variant="primary" size="sm" isDisabled={busy} onPress={() => void send('basic', true)}>
            {say('org.opt.simplify')}
          </Button>
          <Button variant="ghost" size="sm" onPress={() => setSimplifying(null)}>
            {say('common.cancel')}
          </Button>
        </Ask>
      )}
      {refused && (
        <Ask
          title={refused.title ?? say('org.opt.toBasicRefused')}
          lines={refused.problems.length > 0 ? problemLines(refused.problems, say) : undefined}
          said={refused.problems.length > 0 ? undefined : refused.said}
        >
          <Button variant="secondary" size="sm" onPress={() => setRefused(null)}>
            {say('org.opt.keepAdvanced')}
          </Button>
        </Ask>
      )}
      {error && <Notice className="os-ask">{error}</Notice>}
    </Group>
  )
}
